import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import styles from '../pages/JobDashboard/JobDetailNew.module.css'; // Reusing styles
import { API_BASE_URL } from '../config';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Loader2 from '../components/loader2';
import showToast from "../components/ShowToast";
import Swal from 'sweetalert2';
import ModernNavbarPureCSS from "../components/Navbar/ModernNavBarPureCSS";


import {
  ArrowLeft,
  User,
  Camera,
  Search,
  MapPin,
  Phone,
  Home,
  LogOut,
  Plus,
  X,
  Briefcase
} from "lucide-react";

const HomeUserOnboardingNew = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  
  const { userId: userIdFromParams } = useParams(); 
  
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserId(decodedToken.nameid);
      setUsername(decodedToken.UserName || 'User'); 
    }
  }, []);

  // --- State for all onboarding fields ---
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageBase64, setProfileImageBase64] = useState(null);
  const [preview, setPreview] = useState(null);

  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [mobile, setMobile] = useState('');
  const [addressResults, setAddressResults] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);

  const [selectedInterests, setSelectedInterests] = useState([]);
  const [interestSearchTerm, setInterestSearchTerm] = useState('');
  const [interestResults, setInterestResults] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(!!userIdFromParams);

  // Fetch user data if in edit mode
  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      const decodedToken = jwtDecode(token);
      const loggedInUserId = decodedToken.nameid;
      const userType = decodedToken.UserType;

      if (userType != 'Home') {

        showToast('error', 'You are not authorized to access this page.');
        navigate('/newhome');
        return;
      }

      setUserId(loggedInUserId);

      console.log(`Out here with id = ${loggedInUserId}`);
      console.log(`Out here with userIdFromParams = ${String(userIdFromParams)}`);

      if (String(userIdFromParams) == String(loggedInUserId)) {
        console.log("In here");
        setIsLoading(true);
        setIsReadOnly(false); // Allow editing
        try {
          const profileResponse = await axios.get(`${API_BASE_URL}/api/HomeUserProfile/getUserProfileDataOnly?userId=${userIdFromParams}`);
          if (profileResponse.data && profileResponse.data.user) {
            const userData = profileResponse.data.user;
            setFirstName(userData.firstName || '');
            setLastName(userData.lastName || '');
            setMobile(userData.phone || '');
            setAddress(userData.addressComment || '');
            setPostcode(userData.postcode || '');
            setPreview(userData.profilePic);
            
            // Map professions to the selectedInterests state format
            if (userData.professions && userData.professions.length > 0) {
              const interests = userData.professions.map(p => ({
                id: p.professionId,
                name: p.name,
                type: 'Profession'
              }));
              setSelectedInterests(interests);
            }
          }
        } catch (err) {
          showToast('error', 'Failed to load your profile data.');
          console.error(err);
        }
        setIsLoading(false);
      } else if (userIdFromParams) {
        showToast('error', "You can only edit your own profile.");
        setIsReadOnly(true); // Viewing someone else's profile
      }
    };
    fetchData();
  }, [userIdFromParams, navigate]);

  const handleLogout = () => {
      localStorage.removeItem('token');
      localStorage.clear();
      sessionStorage.clear();
      navigate('/login');
  };

  const handleAddressSearchChange = async (e) => {
    const searchValue = e.target.value;
    setAddress(searchValue);
    if (searchValue.length > 2) {
      setIsSearchingAddress(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/GooglePlaces/addresscomplete`, { params: { input: searchValue } });
        setAddressResults(response.data?.predictions || []);
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
      } finally {
        setIsSearchingAddress(false);
      }
    } else {
      setAddressResults([]);
    }
  };

  const handleSelectAddress = async (suggestion) => {
    // Set the full address description in the input field immediately for better UX.
    setAddress(suggestion.description);
    setAddressResults([]); // Close the dropdown

    // Now, fetch the details for the selected place to get the postcode reliably.
    try {
      const response = await axios.get(`${API_BASE_URL}/api/GooglePlaces/placedetails`, { 
        params: { placeId: suggestion.place_id } 
      });
      
      if (response.data && response.data.result && response.data.result.address_components) {
        const addressComponents = response.data.result.address_components;
        const postcodeComponent = addressComponents.find(c => c.types.includes('postal_code'));
        console.log(postcodeComponent);
        if (postcodeComponent) {
          setPostcode(postcodeComponent.long_name);
        } else {
          setPostcode(''); // Clear if no postcode found in the details
        }
      }
    } catch (error) {
      console.error('Error fetching place details:', error);
      showToast('error', 'Could not retrieve address details. Please try again.');
      // Fallback to regex if the details call fails
      const postcodeRegex = /\b\d{4}\b/g;
      const matches = suggestion.description.match(postcodeRegex);
      if (matches && matches.length > 0) {
        setPostcode(matches[matches.length - 1]);
      }
    }
  };

  const handleInterestSearch = async (searchTerm) => {
    setInterestSearchTerm(searchTerm);
    if (searchTerm.length > 0) {
      try {
        // Using the same endpoint as business onboarding for searching professions/services
        const response = await axios.get(`${API_BASE_URL}/api/Search/searchall?search=${searchTerm}`);
        setInterestResults(response.data.slice(0, 30));
      } catch (error) {
        console.error(`Error fetching interests:`, error);
      }
    } else {
      setInterestResults([]);
    }
  };

  const handleSelectInterest = (item) => {
    if (!selectedInterests.some((i) => i.id === item.id && i.type === item.type)) {
      setSelectedInterests([...selectedInterests, item]);
    }
    setInterestSearchTerm('');
    setInterestResults([]);
  };

  const handleRemoveInterest = (itemToRemove) => {
    setSelectedInterests(selectedInterests.filter((item) => !(item.id === itemToRemove.id && item.type === itemToRemove.type)));
  };

  const handleImageUpload = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImageBase64(reader.result);
      };
      reader.readAsDataURL(file);
      setPreview(URL.createObjectURL(file));
      setProfileImage(file);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    if (!firstName || !lastName || !address || !mobile) {
        showToast('error', 'Please fill in all required fields.');
        setIsLoading(false);
        return;
    }

    try {
        // Step 1: Update User Details
        const userDetailsPayload = {
            user_id: userId,
            phone: mobile,
            firstName: firstName,
            lastName: lastName,
            postcode: postcode,
            addressComment: address
        };
        await axios.post(`${API_BASE_URL}/api/HomeOnboarding/update-user-details`, userDetailsPayload);
        showToast('success', 'Personal details saved!');

        // Step 2: Update Profile Picture (if changed)
        if (profileImageBase64) {
            const profilePicPayload = {
                userId: userId,
                imageBase64: profileImageBase64
            };
            await axios.put(`${API_BASE_URL}/api/HomeOnboarding/update-profile-pic`, profilePicPayload);
            showToast('success', 'Profile picture updated!');
        }

        // Step 3: Save Search Selections (Interests)
        const professionIds = selectedInterests.filter(i => i.type === 'Profession').map(i => i.id);
        const serviceIds = selectedInterests.filter(i => i.type === 'Service').map(i => i.id);
        const supplierTypeIds = selectedInterests.filter(i => i.type === 'SupplierType').map(i => i.id);
        const ancillaryIds = selectedInterests.filter(i => i.type === 'ProfessionAncillary').map(i => i.id);

        const selectionsPayload = {
            userId,
            professionIds,
            serviceIds,
            supplierTypeIds,
            ancillaryIds
        };

        if (professionIds.length > 0 || serviceIds.length > 0 || supplierTypeIds.length > 0 || ancillaryIds.length > 0) {
            await axios.post(`${API_BASE_URL}/api/HomeOnboarding/saveSearchSelections`, selectionsPayload);
            showToast('success', 'Your interests have been saved!');
        }

        // Step 4: Mark onboarding as complete (for new users only)
        try {
            const response = await axios.post(`${API_BASE_URL}/api/HomeOnboarding/setOnboardingComplete?userId=${userId}`);
            console.log("Onboarding API response:", response.data);
        } catch (error) {
            console.error("Onboarding API error:", error);
        }

        // Final success message
        Swal.fire({
            title: "Success!",
            text: `Your profile has been ${userIdFromParams ? 'updated' : 'created'} successfully!`,
            icon: "success",
            confirmButtonText: "Go to Dashboard",
        }).then(() => {
            if (!userIdFromParams) {
                localStorage.setItem('shouldOnboard', "0");
            }
            navigate('/newhome');
        });

    } catch (err) {
        showToast('error', 'An error occurred while saving your profile. Please try again.');
        console.error("Error submitting form:", err.response ? err.response.data : err);
    } finally {
        setIsLoading(false);
    }
  };

  return (
    <div className={`${styles.jobDetailNew_minHeightScreen} ${styles.jobDetailNew_bgGradientPage}`}>

      <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'profile'} />

      <header className={styles.jobDetailNew_header}>
        <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_headerContent}`}>
          <div className={styles.jobDetailNew_headerLeft}>
            {userIdFromParams && (
              <button onClick={() => navigate(-1)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost}`} style={{ color: 'Navy' }}>
                <ArrowLeft className={styles.jobDetailNew_iconMd} />
              </button>
            )}
            <div className={styles.jobDetailNew_headerLogo}>
              <Home className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textAmber400}`} />
              <span className={styles.jobDetailNew_headerTitle}>Build Start</span>
            </div>
            <div className={styles.jobDetailNew_headerSeparator}></div>
            <div>
              <h1 className={styles.jobDetailNew_headerTitle}>{userIdFromParams ? "Edit Your Profile" : "Welcome! Let's Get Started"}</h1>
              <p className={styles.jobDetailNew_textBlue900}>{userIdFromParams ? "Update your personal details" : "Complete your profile to find the best services"}</p>
            </div>
          </div>
          <div className={styles.jobDetailNew_headerRight}>
            <button onClick={handleLogout} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost}`} style={{ color: 'Navy' }}>
              <LogOut className={styles.jobDetailNew_iconMd} />
              <span className={styles.jobDetailNew_ml2}>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_mainContent}`}>
        {isLoading && <Loader2 />}
        <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_lg_gridCols3}`}>
          <div className={`${styles.jobDetailNew_lg_colSpan2} ${styles.jobDetailNew_spaceY6}`}>
            
            {/* --- Personal Details Card --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={`${styles.jobDetailNew_cardHeader} ${styles.jobDetailNew_cardHeaderGradient}`}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <User className={styles.jobDetailNew_iconMd} />
                  <span>Personal Details</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem'}}>
                  <div>
                    <label htmlFor="firstName" className={styles.jobDetailNew_label}>First Name</label>
                    <input id="firstName" type="text" placeholder="Your first name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={styles.jobDetailNew_input} disabled={isReadOnly} />
                  </div>
                  <div>
                    <label htmlFor="lastName" className={styles.jobDetailNew_label}>Last Name</label>
                    <input id="lastName" type="text" placeholder="Your last name" value={lastName} onChange={(e) => setLastName(e.target.value)} className={styles.jobDetailNew_input} disabled={isReadOnly} />
                  </div>
                </div>
              </div>
            </div>

            {/* --- Contact Information Card --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={`${styles.jobDetailNew_cardHeader} ${styles.jobDetailNew_cardHeaderGradient}`}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <MapPin className={styles.jobDetailNew_iconMd} />
                  <span>Contact Information</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                 <div>
                    <label htmlFor="mobile" className={styles.jobDetailNew_label}>Mobile</label>
                    <input id="mobile" type="tel" placeholder="04xxxxxxxx" value={mobile} onChange={(e) => setMobile(e.target.value)} className={styles.jobDetailNew_input} disabled={isReadOnly} />
                  </div>
                  <div>
                    <label htmlFor="address" className={styles.jobDetailNew_label}>Your Address</label>
                    <div className={styles.jobDetailNew_inputWithIconContainer}>
                      <Search className={styles.jobDetailNew_inputIcon} />
                      <input id="address" type="text" placeholder="Search for your address" value={address} onChange={handleAddressSearchChange} className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`} disabled={isReadOnly} />
                    </div>
                    {addressResults.length > 0 && (
                        <ul className={styles.jobDetailNew_dropdownResults}>
                          {addressResults.map((suggestion) => (
                            <li key={suggestion.place_id} className={styles.jobDetailNew_resultItem} onClick={() => handleSelectAddress(suggestion)}>
                              {suggestion.description}
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
              </div>
            </div>

            {/* --- Interests Card --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Briefcase className={styles.jobDetailNew_iconMd} />
                  <span>What are you looking for?</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div>
                  <label className={styles.jobDetailNew_label}>Search for professions or services you're interested in</label>
                  <div className={styles.jobDetailNew_inputWithIconContainer}>
                    <Search className={styles.jobDetailNew_inputIcon} />
                    <input
                      type="text"
                      placeholder="e.g., Plumber, Electrician, Landscaping..."
                      className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`}
                      value={interestSearchTerm}
                      onChange={(e) => {
                        setInterestSearchTerm(e.target.value);
                        handleInterestSearch(e.target.value);
                      }}
                      disabled={isReadOnly}
                    />
                     {interestResults.length > 0 && (
                      <ul className={styles.jobDetailNew_dropdownResults}>
                        {interestResults.map((item) => (
                          <li key={`${item.id}-${item.type}`} className={styles.jobDetailNew_resultItem} onClick={() => handleSelectInterest(item)}>
                            {item.name} ({item.type})
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className={styles.jobDetailNew_flexWrap} style={{ marginTop: '1rem' }}>
                    {selectedInterests.map((interest) => (
                      <span key={`${interest.id}-${interest.type}`} className={styles.jobDetailNew_badge}>
                        {interest.name} ({interest.type})
                        {!isReadOnly && (
                          <button type="button" onClick={() => handleRemoveInterest(interest)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost} ${styles.jobDetailNew_buttonSm}`}>
                            <X className={styles.jobDetailNew_buttonIcon} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* --- Profile Image Card --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Camera className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textAmber600}`} />
                  <span>Profile Image</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY4}`}>
                <p className={`${styles.jobDetailNew_textSm} ${styles.jobDetailNew_textGray600}`}>Upload a profile picture.</p>
                <div className={styles.jobDetailNew_uploadArea}>
                  <input id="profile-image-upload" type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={isReadOnly} />
                  <label htmlFor="profile-image-upload" className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`} style={{cursor: isReadOnly ? 'default' : 'pointer'}}>
                    Browse File
                  </label>
                </div>
                {preview && (
                  <div className={styles.jobDetailNew_uploadedImagesGrid}>
                    <div className={styles.jobDetailNew_uploadedImageItem}>
                      <img src={preview} alt="Profile Preview" className={styles.jobDetailNew_uploadedImage} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.jobDetailNew_spaceY6}>
            <div className={`${styles.jobDetailNew_card} ${styles.jobDetailNew_sidebarCard}`}>
              <div className={`${styles.jobDetailNew_cardHeader} ${styles.jobDetailNew_sidebarHeader}`}>
                <h3 className={styles.jobDetailNew_sidebarTitle}>Profile Summary</h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_sidebarContentSpaceY4}`}>
                <div className={styles.jobDetailNew_sidebarContentSpaceY3}>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>NAME</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{firstName} {lastName || "Not specified"}</p>
                  </div>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>LOCATION</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{address || "Not specified"}</p>
                  </div>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>INTERESTS</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{selectedInterests.length} selected</p>
                  </div>
                </div>
                <div className={styles.jobDetailNew_separator}></div>
                <div className={styles.jobDetailNew_sidebarContentSpaceY3}>
                  {!isReadOnly && (
                    <button type="button" onClick={handleSubmit} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonFullWidth} ${styles.jobDetailNew_buttonPrimary}`}>
                      <User className={`${styles.jobDetailNew_iconBase} ${styles.jobDetailNew_buttonIconMr2}`} />
                      {userIdFromParams ? "Save Changes" : "Complete Onboarding"}
                    </button>
                  )}
                  {userIdFromParams && (
                    <button type="button" onClick={() => navigate(-1)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonFullWidth} ${styles.jobDetailNew_buttonOutline}`}>
                      <ArrowLeft className={`${styles.jobDetailNew_iconBase} ${styles.jobDetailNew_buttonIconMr2}`} />
                      Go Back
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeUserOnboardingNew;
